import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { type Role } from "./shared/constants";
import LoginPage from "./pages/LoginPage";
import PlayerDashboard from "./pages/PlayerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import { TimerProvider } from "./contexts/TimerContext";

interface UserData {
  playerId: string;
  gameName: string;
  teamName: string;
  teamCodeName: string;
  role: Role;
  loginCode: string;
  communicationLevel: number | null;
  scanningLevel: number | null;
  spaceshipDamage?: number | null;
  personalScore: number;
  accessToken: string;
  refreshToken: string;
}

interface AdminData {
  adminId: string;
  userName: string;
  accessToken: string;
  refreshToken: string;
}

// Protected route wrapper for players
function ProtectedRoute({ children, userData, isBootstrapping }: { children: React.ReactNode; userData: UserData | null; isBootstrapping: boolean }) {
  if (isBootstrapping) {
    return null;
  }
  if (!userData) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Protected route wrapper for admins
function ProtectedAdminRoute({ children, adminData, isBootstrapping }: { children: React.ReactNode; adminData: AdminData | null; isBootstrapping: boolean }) {
  if (isBootstrapping) {
    return null;
  }
  if (!adminData) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const navigate = useNavigate();
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true);

  // Load user data from localStorage on mount (for persistence across refreshes)
  useEffect(() => {
    const savedUserData = localStorage.getItem("userData");
    const playerAccessToken = localStorage.getItem("playerAccessToken");
    const playerRefreshToken = localStorage.getItem("playerRefreshToken");
    
    if (savedUserData && playerAccessToken && playerRefreshToken) {
      try {
        const userInfo = JSON.parse(savedUserData);
        setUserData({
          ...userInfo,
          personalScore: userInfo.personalScore ?? 0,
          accessToken: playerAccessToken,
          refreshToken: playerRefreshToken
        });
      } catch (e) {
        console.error("Failed to load user data:", e);
        localStorage.removeItem("userData");
        localStorage.removeItem("playerAccessToken");
        localStorage.removeItem("playerRefreshToken");
      }
    }
    
    // Load admin data and tokens
    const savedAdminData = localStorage.getItem("adminData");
    const accessToken = localStorage.getItem("adminAccessToken");
    const refreshToken = localStorage.getItem("adminRefreshToken");
    
    if (savedAdminData && accessToken && refreshToken) {
      try {
        const adminInfo = JSON.parse(savedAdminData);
        setAdminData({
          ...adminInfo,
          accessToken,
          refreshToken
        });
      } catch (e) {
        console.error("Failed to load admin data:", e);
        localStorage.removeItem("adminData");
        localStorage.removeItem("adminAccessToken");
        localStorage.removeItem("adminRefreshToken");
      }
    }
    setIsBootstrapping(false);
  }, []);

  const handleLogin = (loginData: UserData) => {
    setUserData(loginData);
    // Save to localStorage for persistence (without tokens in main object)
    localStorage.setItem("userData", JSON.stringify({
      playerId: loginData.playerId,
      gameName: loginData.gameName,
      teamName: loginData.teamName,
      teamCodeName: loginData.teamCodeName,
      role: loginData.role,
      loginCode: loginData.loginCode,
      communicationLevel: loginData.communicationLevel,
      scanningLevel: loginData.scanningLevel,
      spaceshipDamage: loginData.spaceshipDamage ?? null,
      personalScore: loginData.personalScore
    }));
    // Store tokens separately for security
    localStorage.setItem("playerAccessToken", loginData.accessToken);
    localStorage.setItem("playerRefreshToken", loginData.refreshToken);
    // Navigate to dashboard
    navigate("/player_dashboard");
  };

  const handleAdminLogin = (loginData: AdminData) => {
    setAdminData(loginData);
    // Save to localStorage for persistence
    localStorage.setItem("adminData", JSON.stringify({
      adminId: loginData.adminId,
      userName: loginData.userName
    }));
    // Store tokens separately for security
    localStorage.setItem("adminAccessToken", loginData.accessToken);
    localStorage.setItem("adminRefreshToken", loginData.refreshToken);
    // Navigate to admin dashboard
    navigate("/admin_dashboard");
  };

  const handleLogout = () => {
    // Clear player data cache on logout
    if (userData?.playerId) {
      localStorage.removeItem(`lost-in-space-player-data-${userData.playerId}`);
    }
    // Clear all player data cache entries (in case of multiple players)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("lost-in-space-player-data-")) {
        localStorage.removeItem(key);
      }
    });
    
    setUserData(null);
    localStorage.removeItem("userData");
    localStorage.removeItem("playerAccessToken");
    localStorage.removeItem("playerRefreshToken");
    // Navigate to login
    navigate("/login");
  };

  const handleAdminLogout = () => {
    setAdminData(null);
    localStorage.removeItem("adminData");
    localStorage.removeItem("adminAccessToken");
    localStorage.removeItem("adminRefreshToken");
    // Navigate to login
    navigate("/login");
  };

  return (
    <Routes>
      <Route 
        path="/login" 
        element={<LoginPage onLogin={handleLogin} onAdminLogin={handleAdminLogin} />} 
      />
      <Route
        path="/player_dashboard"
        element={
          <ProtectedRoute userData={userData} isBootstrapping={isBootstrapping}>
            {userData && (
              <PlayerDashboard
                playerId={userData.playerId}
                onLogout={handleLogout}
              />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin_dashboard"
        element={
          <ProtectedAdminRoute adminData={adminData} isBootstrapping={isBootstrapping}>
            {adminData && (
              <AdminDashboard
                adminId={adminData.adminId}
                userName={adminData.userName}
                onLogout={handleAdminLogout}
              />
            )}
          </ProtectedAdminRoute>
        }
      />
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <TimerProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TimerProvider>
  );
}

export default App;
